import React, { useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Combobox, Group, Pill, PillsInput, useCombobox } from '@mantine/core';
import { t } from 'i18next';
import { IQuestionnaires } from '../../../global/entities/Questionnaires/Questionnaires';

export type IQuestionnaireMultiSelectProps = {
    value?: number[];
    onChange: (values: number[]) => void;
    isError: boolean;
    questionnairesData?: IQuestionnaires[];
    placeholder?: string;
};

const QuestionnaireMultiSelect: React.FC<IQuestionnaireMultiSelectProps> = inject()(
    observer(({ value = [], onChange, isError, questionnairesData = [], placeholder }) => {
        const [search, setSearch] = useState('');

        const combobox = useCombobox({
            onDropdownClose: () => combobox.resetSelectedOption(),
            onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
        });

        const handleToggleQuestionnaire = (questionnaireId: number) => {
            if (value.includes(questionnaireId)) 
                onChange(value.filter(id => id !== questionnaireId));
             else 
                onChange([...value, questionnaireId]);
            
        };

        const handleValueRemove = (val: string) => {
            onChange(value.filter(id => id !== parseInt(val)));
        };

        const filteredOptions = useMemo(() => {
            return questionnairesData.filter(questionnaire =>
                questionnaire.name.toLowerCase().includes(search.toLowerCase())
            );
        }, [questionnairesData, search]);

        const options = filteredOptions.map((item) => {
            const isSelected = value.includes(item.id);
            return (
                <Combobox.Option value={item.id.toString()} key={item.id}>
                    <Group gap="sm">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            style={{ pointerEvents: 'none' }}
                        />
                        <span>{item.name}</span>
                    </Group>
                </Combobox.Option>
            );
        });

        const pills = value.map((selectedId) => {
            const questionnaire = questionnairesData.find(q => q.id === selectedId);
            if (!questionnaire) return null;
            
            return (
                <Pill key={selectedId} withRemoveButton onRemove={() => handleValueRemove(selectedId.toString())}>
                    {questionnaire.name}
                </Pill>
            );
        }).filter(Boolean);

        return (
            <Combobox
                store={combobox}
                onOptionSubmit={(val) => {
                    handleToggleQuestionnaire(parseInt(val));
                    setSearch('');
                }}
            >
                <Combobox.DropdownTarget>
                    <PillsInput onClick={() => combobox.openDropdown()} error={isError}>
                        <Pill.Group>
                            {pills}
                            <Combobox.EventsTarget>
                                <PillsInput.Field
                                    placeholder={placeholder}
                                    value={search}
                                    onChange={(event) => {
                                        combobox.updateSelectedOptionIndex();
                                        setSearch(event.currentTarget.value);
                                    }}
                                    onFocus={() => combobox.openDropdown()}
                                    onBlur={() => combobox.closeDropdown()}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Backspace' && search.length === 0) {
                                            event.preventDefault();
                                            if (value.length > 0) 
                                                handleValueRemove(value[value.length - 1].toString());
                                            
                                        }
                                    }}
                                />
                            </Combobox.EventsTarget>
                        </Pill.Group>
                    </PillsInput>
                </Combobox.DropdownTarget>

                <Combobox.Dropdown>
                    <Combobox.Options>
                        {options.length > 0 ? (
                            options
                        ) : (
                            <Combobox.Empty>{t('formHcf.noQuestionnairesFound')}</Combobox.Empty>
                        )}
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>
        );
    }),
);

export default QuestionnaireMultiSelect;
